import _slicedToArray from "@babel/runtime/helpers/slicedToArray";

function _createForOfIteratorHelper(o, allowArrayLike) { var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"]; if (!it) { if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = it.call(o); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it.return != null) it.return(); } finally { if (didErr) throw err; } } }; }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

import { useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import errorRecord from '@magento/peregrine/lib/util/createErrorRecord';
import { useAppContext } from '@magento/peregrine/lib/context/app';
var dismissers = new WeakMap(); // Memoize dismisser funcs to reduce re-renders from func identity change.

var getErrorDismisser = function getErrorDismisser(error, onDismissError) {
  return dismissers.has(error) ? dismissers.get(error) : dismissers.set(error, function () {
    return onDismissError(error);
  }).get(error);
};
/**
 * Talon that handles effects for App and returns props necessary for rendering
 * the app.
 *
 * @param {Function} props.handleError callback to invoke for each error
 * @param {Function} props.handleIsOffline callback to invoke when the app goes offline
 * @param {Function} props.handleIsOnline callback to invoke wen the app goes online
 * @param {Function} props.handleHTMLUpdate callback to invoke when a HTML update is available
 * @param {Function} props.markErrorHandled callback to invoke when handling an error
 * @param {Function} props.renderError an error that occurs during rendering of the app
 * @param {Function} props.unhandledErrors errors coming from the error reducer
 *
 * @returns {{
 *  hasOverlay: boolean
 *  handleCloseDrawer: function
 * }}
 */


export var useApp = function useApp(props) {
  var handleError = props.handleError,
      handleIsOffline = props.handleIsOffline,
      handleIsOnline = props.handleIsOnline,
      markErrorHandled = props.markErrorHandled,
      renderError = props.renderError,
      unhandledErrors = props.unhandledErrors;
  var history = useHistory();
  var reload = useCallback(function () {
    if (process.env.NODE_ENV !== 'development') {
      history.go(0);
    }
  }, [history]);
  var renderErrors = useMemo(function () {
    return renderError ? [errorRecord(renderError, globalThis, useApp, renderError.stack)] : [];
  }, [renderError]);
  var errors = renderError ? renderErrors : unhandledErrors;
  var handleDismissError = renderError ? reload : markErrorHandled; // Only add toasts for errors if the errors list changes. Since `addToast`
  // and `toasts` changes each render we cannot add it as an effect dependency
  // otherwise we infinitely loop.

  useEffect(function () {
    var _iterator = _createForOfIteratorHelper(errors),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var _ref2 = _step.value;
        var error = _ref2.error,
            id = _ref2.id,
            loc = _ref2.loc;
        handleError(error, id, loc, getErrorDismisser(error, handleDismissError));
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }
  }, [errors, handleDismissError, handleError]);

  var _useAppContext = useAppContext(),
      _useAppContext2 = _slicedToArray(_useAppContext, 2),
      appState = _useAppContext2[0],
      appApi = _useAppContext2[1];

  var closeDrawer = appApi.closeDrawer;
  var hasBeenOffline = appState.hasBeenOffline,
      isOnline = appState.isOnline,
      overlay = appState.overlay;
  useEffect(function () {
    if (hasBeenOffline) {
      if (isOnline) {
        handleIsOnline();
      } else {
        handleIsOffline();
      }
    }
  }, [handleIsOnline, handleIsOffline, hasBeenOffline, isOnline]);
  var handleCloseDrawer = useCallback(function () {
    closeDrawer();
  }, [closeDrawer]);
  return {
    hasOverlay: !!overlay,
    handleCloseDrawer: handleCloseDrawer
  };
};